import * as React from "react";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "./ui/tooltip";
import { Button } from "./ui/button";
import { Loader } from "lucide-react";
import { BsArrowReturnLeft, BsStar } from "react-icons/bs";
import { FaRegCopy } from "react-icons/fa";
import { TbFileExport } from "react-icons/tb";
import { FaRegFilePdf } from "react-icons/fa";
import type { ChatMessage } from "../../types/discuss.types";
import type { ResearchChatMessage } from "../../types/research.types";
import { useSubmitFeedback } from "../../hooks/useSubmitFeedback";
import { useMessageActionsWithLoading, MessageDialogs } from "./message-actions.utils";

type Message = ChatMessage | ResearchChatMessage;

export interface MessageActionsProps {
  messageText: string;
  showRating?: boolean;
  showSaveToBank?: boolean;
  showSaveToLibrary?: boolean;
  showCreateDocument?: boolean;
  message: Message;
  prevMessage?: Message;
  documentId?: string;
  documentTitle?: string;
  source: "discuss" | "research";
}

interface ActionItemProps {
  icon: React.ReactNode;
  tooltipContent: string;
  onClick: () => void;
  disabled?: boolean;
  isLoading?: boolean;
}

const ActionItem: React.FC<ActionItemProps> = ({
  icon,
  tooltipContent,
  onClick,
  disabled = false,
  isLoading = false,
}) => {
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <span>
          <Button
            className="p-0 size-auto"
            variant="ghost"
            size="icon"
            onClick={onClick}
            disabled={disabled || isLoading}
          >
            {isLoading ? <Loader className="size-[14px] animate-spin" /> : icon}
          </Button>
        </span>
      </TooltipTrigger>
      <TooltipContent>{tooltipContent}</TooltipContent>
    </Tooltip>
  );
};

const MessageActionsToolbar: React.FC<{
  showCreateDocument: boolean;
  showRating: boolean;
  showSaveToLibrary: boolean;
  messageText: string;
  source: "discuss" | "research";
  handleCreateDocumentFromMessage: (text: string, title: string) => void;
  handleCopyMessage: (text: string) => void;
  handleInsertMessageToDocument: (text: string) => void;
  onRatingClick: () => void;
  onSaveToLibrary: () => void;
  isCreatingDocument: boolean;
  isCopying: boolean;
  isInserting: boolean;
}> = ({
  showCreateDocument,
  showRating,
  showSaveToLibrary,
  messageText,
  source,
  handleCreateDocumentFromMessage,
  handleCopyMessage,
  handleInsertMessageToDocument,
  onRatingClick,
  onSaveToLibrary,
  isCreatingDocument,
  isCopying,
  isInserting,
}) => {
  const isAnyLoading = isCreatingDocument || isCopying || isInserting;
  return (
    <div className="flex flex-col items-center gap-3">
      {showCreateDocument && (
        <ActionItem
          icon={<FaRegFilePdf className="size-[14px]" />}
          tooltipContent="Create document from message"
          isLoading={isCreatingDocument}
          disabled={isAnyLoading}
          onClick={() => {
            const title = source === "discuss" ? "MS Word Discussion" : "MS Word Research";
            handleCreateDocumentFromMessage(messageText, title);
          }}
        />
      )}
      <ActionItem
        icon={<FaRegCopy className="size-[14px]" />}
        tooltipContent="Copy text"
        isLoading={isCopying}
        disabled={isAnyLoading}
        onClick={() => handleCopyMessage(messageText)}
      />
      <ActionItem
        icon={<BsArrowReturnLeft className="size-[14px]" />}
        tooltipContent="Insert text"
        isLoading={isInserting}
        disabled={isAnyLoading}
        onClick={() => handleInsertMessageToDocument(messageText)}
      />
      {showRating && (
        <ActionItem
          icon={<BsStar className="size-[14px]" />}
          tooltipContent="Rate response"
          disabled={isAnyLoading}
          onClick={onRatingClick}
        />
      )}
      {showSaveToLibrary && (
        <ActionItem
          icon={<TbFileExport className="size-[14px]" />}
          tooltipContent="Save to request library"
          disabled={isAnyLoading}
          onClick={onSaveToLibrary}
        />
      )}
    </div>
  );
};

const MessageActions: React.FC<MessageActionsProps> = ({
  messageText,
  showRating = true,
  showSaveToLibrary = true,
  showCreateDocument = true,
  message,
  prevMessage,
  documentId,
  documentTitle,
  source,
}) => {
  const [showRatingDialog, setShowRatingDialog] = React.useState(false);
  const [isSubmitting, setIsSubmitting] = React.useState(false);
  const { mutateAsync: submitFeedback } = useSubmitFeedback();
  const [isCreateRequestOpen, setIsCreateRequestOpen] = React.useState(false);
  const [createRequestData, setCreateRequestData] = React.useState<
    { name: string; content: string } | undefined
  >(undefined);

  const {
    handleCopyMessage,
    handleInsertMessageToDocument,
    handleCreateDocumentFromMessage,
    isCopying,
    isInserting,
    isCreatingDoc,
  } = useMessageActionsWithLoading(documentId, documentTitle);

  return (
    <TooltipProvider>
      <MessageActionsToolbar
        showCreateDocument={showCreateDocument}
        showRating={showRating}
        showSaveToLibrary={showSaveToLibrary}
        messageText={messageText}
        source={source}
        handleCreateDocumentFromMessage={handleCreateDocumentFromMessage}
        handleCopyMessage={handleCopyMessage}
        handleInsertMessageToDocument={handleInsertMessageToDocument}
        onRatingClick={() => setShowRatingDialog(true)}
        onSaveToLibrary={() => {
          setCreateRequestData({ name: "", content: messageText });
          setIsCreateRequestOpen(true);
        }}
        isCreatingDocument={isCreatingDoc}
        isCopying={isCopying}
        isInserting={isInserting}
      />
      <MessageDialogs
        showRatingDialog={showRatingDialog}
        setShowRatingDialog={setShowRatingDialog}
        isSubmitting={isSubmitting}
        ratingDeps={{
          documentId,
          documentTitle,
          source,
          message,
          prevMessage,
          submitFeedback,
          setIsSubmitting,
          setShowRatingDialog,
        }}
        isCreateRequestOpen={isCreateRequestOpen}
        setIsCreateRequestOpen={setIsCreateRequestOpen}
        createRequestData={createRequestData}
        source={source}
      />
    </TooltipProvider>
  );
};

export default MessageActions;
